/**
 * Civilhealth Healthcare - Reusable KPI Card Component
 * High Visibility Icon Design - WCAG AA Compliant
 *
 * Rule: Icons ALWAYS use dark colors on light backgrounds
 * Background colors are light pastels, icons are dark shades
 */

import React from 'react';
import { Box, Typography, Card, CardContent, useTheme, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import { kpiColors, statusColors } from '../../theme/statusColors';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'purple' | 'pink';
  trend?: {
    value: number;
    isPositive: boolean;
  };
  onClick?: () => void;
  loading?: boolean;
}

const colorMap = {
  primary: {
    bg: kpiColors.appointments.bg,
    icon: kpiColors.appointments.icon,
    gradient: kpiColors.appointments.gradient,
    solid: kpiColors.appointments.solid,
  },
  success: {
    bg: kpiColors.patients.bg,
    icon: kpiColors.patients.icon,
    gradient: kpiColors.patients.gradient,
    solid: kpiColors.patients.solid,
  },
  warning: {
    bg: kpiColors.reports.bg,
    icon: kpiColors.reports.icon,
    gradient: kpiColors.reports.gradient,
    solid: kpiColors.reports.solid,
  },
  error: {
    bg: statusColors.error.bg,
    icon: statusColors.error.icon,
    gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    solid: '#EF4444',
  },
  info: {
    bg: kpiColors.appointments.bg,
    icon: kpiColors.appointments.icon,
    gradient: kpiColors.appointments.gradient,
    solid: kpiColors.appointments.solid,
  },
  purple: {
    bg: kpiColors.notifications.bg,
    icon: kpiColors.notifications.icon,
    gradient: kpiColors.notifications.gradient,
    solid: kpiColors.notifications.solid,
  },
  pink: {
    bg: kpiColors.analytics.bg,
    icon: kpiColors.analytics.icon,
    gradient: kpiColors.analytics.gradient,
    solid: kpiColors.analytics.solid,
  },
};

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  trend,
  onClick,
  loading = false,
}) => {
  const theme = useTheme();
  const colors = colorMap[color];
  const isClickable = !!onClick;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={isClickable ? { scale: 1.02 } : {}}
    >
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E7EB',
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          background: '#FFFFFF',
          '&:hover': isClickable
            ? {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                borderColor: colors.solid,
              }
            : {},
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontWeight: 500,
                  mb: 0.5,
                  fontSize: '0.875rem',
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1A1A2E',
                  mb: 0.5,
                  lineHeight: 1.2,
                }}
              >
                {loading ? '...' : value}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6B7280',
                    fontSize: '0.75rem',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: trend.isPositive ? '#15803D' : '#DC2626',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    vs last month
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Icon Container - High Contrast Design */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Light background for maximum icon contrast
                bgcolor: colors.bg,
                // Colored border for visual interest
                border: `2px solid ${alpha(colors.solid, 0.3)}`,
                // Smooth transition on hover
                transition: 'all 0.3s ease',
                '& svg': {
                  fontSize: 28,
                  // DARK icon color on LIGHT background - ALWAYS visible
                  color: colors.icon,
                },
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default KPICard;